// Model editor — "Saved presets" tab.
//
// Lets a model author define named OGS and Leveling presets: reusable sets of
// slider values that show up as one-click options at the top of the scenario
// intake's OGS / Leveling tabs. Reuses the same row editors as the intake
// (OgsDisciplineRow, LevelMixRow) so a preset is authored with the exact same
// controls the end user tunes.

import { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import type {
  LevelMixOverride,
  LevelMixPreset,
  Model,
  OgsDisciplineConfig,
  OgsPreset,
} from '@rc/engine/types';
import OgsDisciplineRow from '../intake/OgsCard';
import LevelMixRow from '../intake/LevelMixCard';

interface Props {
  model: Model;
  onChange: (m: Model) => void;
}

function makeId(prefix: string): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `${prefix}_${rand}`;
}

export default function SavedPresetsEditor({ model, onChange }: Props) {
  return (
    <div className="space-y-8">
      <p className="text-xs text-slate-500 max-w-2xl">
        Presets are named, reusable slider configurations. Each one appears as a one-click option
        at the top of the scenario intake's OGS / Leveling tab. Selecting a preset copies these
        values onto the scenario; the user can still fine-tune afterwards. If a model defines no
        presets, the intake simply shows the sliders (fully custom).
      </p>
      <OgsPresetsSection model={model} onChange={onChange} />
      <LevelingPresetsSection model={model} onChange={onChange} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// OGS presets
// ---------------------------------------------------------------------------
function OgsPresetsSection({ model, onChange }: Props) {
  const presets = model.ogsPresets || [];
  const disciplines = (model.disciplines || []).filter((d) => d.active !== false);
  const hasHubs = (model.ogsHubs || []).length > 0;

  const update = (next: OgsPreset[]) => onChange({ ...model, ogsPresets: next });

  const add = () => {
    update([...presets, { id: makeId('ogs'), name: '', disciplines: [] }]);
  };

  const rename = (id: string, name: string) =>
    update(presets.map((p) => (p.id === id ? { ...p, name } : p)));
  const remove = (id: string) => update(presets.filter((p) => p.id !== id));

  const setConfig = (presetId: string, next: OgsDisciplineConfig) => {
    update(
      presets.map((p) => {
        if (p.id !== presetId) return p;
        const nextCh = next.channelId ?? 'all';
        const idx = p.disciplines.findIndex(
          (d) => d.disciplineId === next.disciplineId && (d.channelId ?? 'all') === nextCh,
        );
        const list =
          idx >= 0
            ? p.disciplines.map((d, i) => (i === idx ? next : d))
            : [...p.disciplines, next];
        return { ...p, disciplines: list };
      }),
    );
  };

  return (
    <PresetSection
      title="OGS presets"
      description="Predefined OGS % / hub / role selections across disciplines."
      count={presets.length}
      onAdd={add}
    >
      {!hasHubs ? (
        <p className="text-xs text-amber-700">
          This model has no OGS hubs configured, so OGS presets cannot be authored. Add hubs on the
          Buckets &amp; hubs tab.
        </p>
      ) : presets.length === 0 ? (
        <EmptyState label="No OGS presets yet." />
      ) : (
        presets.map((preset) => (
          <PresetCard
            key={preset.id}
            name={preset.name}
            onRename={(n) => rename(preset.id, n)}
            onRemove={() => remove(preset.id)}
          >
            <div className="space-y-2">
              {disciplines.map((d) => (
                <OgsDisciplineRow
                  key={d.id}
                  discipline={d}
                  model={model}
                  configs={preset.disciplines}
                  onChange={(next) => setConfig(preset.id, next)}
                />
              ))}
            </div>
          </PresetCard>
        ))
      )}
    </PresetSection>
  );
}

// ---------------------------------------------------------------------------
// Leveling presets
// ---------------------------------------------------------------------------
function LevelingPresetsSection({ model, onChange }: Props) {
  const presets = model.levelMixPresets || [];
  const disciplines = (model.disciplines || []).filter((d) => d.active !== false);

  const update = (next: LevelMixPreset[]) => onChange({ ...model, levelMixPresets: next });

  const add = () => {
    update([...presets, { id: makeId('lvl'), name: '', overrides: [] }]);
  };

  const rename = (id: string, name: string) =>
    update(presets.map((p) => (p.id === id ? { ...p, name } : p)));
  const remove = (id: string) => update(presets.filter((p) => p.id !== id));

  const setOverride = (presetId: string, next: LevelMixOverride) => {
    update(
      presets.map((p) => {
        if (p.id !== presetId) return p;
        const nextCh = next.channelId ?? 'all';
        const idx = p.overrides.findIndex(
          (o) => o.disciplineId === next.disciplineId && (o.channelId ?? 'all') === nextCh,
        );
        let list: LevelMixOverride[];
        if (next.shift === 0) {
          list = p.overrides.filter((_, i) => i !== idx);
        } else if (idx >= 0) {
          list = p.overrides.map((o, i) => (i === idx ? next : o));
        } else {
          list = [...p.overrides, next];
        }
        return { ...p, overrides: list };
      }),
    );
  };

  return (
    <PresetSection
      title="Leveling presets"
      description="Predefined seniority shifts (Junior ↔ Executive) across disciplines."
      count={presets.length}
      onAdd={add}
    >
      {presets.length === 0 ? (
        <EmptyState label="No Leveling presets yet." />
      ) : (
        presets.map((preset) => (
          <PresetCard
            key={preset.id}
            name={preset.name}
            onRename={(n) => rename(preset.id, n)}
            onRemove={() => remove(preset.id)}
          >
            <div className="space-y-2">
              {disciplines.map((d) => (
                <LevelMixRow
                  key={d.id}
                  discipline={d}
                  model={model}
                  configs={preset.overrides}
                  onChange={(next) => setOverride(preset.id, next)}
                />
              ))}
            </div>
          </PresetCard>
        ))
      )}
    </PresetSection>
  );
}

// ---------------------------------------------------------------------------
// Shared presentational pieces
// ---------------------------------------------------------------------------
function PresetSection({
  title,
  description,
  count,
  onAdd,
  children,
}: {
  title: string;
  description: string;
  count: number;
  onAdd: () => void;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">
            {title}
            <span className="ml-2 text-xs font-normal text-slate-400">{count}</span>
          </h3>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
        <button type="button" className="btn-secondary" onClick={onAdd}>
          <Plus size={14} /> Add preset
        </button>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/50 px-4 py-6 text-center text-xs text-slate-500">
      {label} Click <span className="font-medium">Add preset</span> to create one.
    </div>
  );
}

function PresetCard({
  name,
  onRename,
  onRemove,
  children,
}: {
  name: string;
  onRename: (name: string) => void;
  onRemove: () => void;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  const nameMissing = !name.trim();
  return (
    <div className={`rounded-lg border ${nameMissing ? 'border-rose-300' : 'border-slate-200'}`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50/60">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-slate-400 hover:text-slate-600 shrink-0"
          title={open ? 'Collapse' : 'Expand'}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <div className="flex flex-col">
          <input
            className={`input py-1 px-2 text-sm font-medium max-w-xs ${
              nameMissing ? 'border-rose-300 focus:border-rose-400' : ''
            }`}
            value={name}
            onChange={(e) => onRename(e.target.value)}
            placeholder="Preset name (required)"
            autoFocus={nameMissing}
          />
          {nameMissing && (
            <span className="text-[11px] text-rose-600 mt-0.5">Name required before saving</span>
          )}
        </div>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onRemove}
          className="btn-ghost text-xs text-rose-600 hover:bg-rose-50"
          title="Delete preset"
        >
          <Trash2 size={14} /> Delete
        </button>
      </div>
      {open && <div className="p-3">{children}</div>}
    </div>
  );
}
